---
title: Equalizer
description: "An equalizer device: four bands with a type, a frequency, a gain and a width, a panel with a handle on each band, a Web Audio engine and tools for agents."
component: true
base: base
---

<ComponentPreview styleName="base-nova" name="block-equalizer" />

Shapes the spectrum with four bands in series, then an output level. The panel draws the filters the engine runs, so what you see is what you hear. It is a [device](/docs/devices): a state, a panel, an engine and tools, each its own file.

## Installation

<Install name="equalizer" kind="device" />

## Controls

<DeviceControls device="equalizer">
<Control id="lowType">The shape of the first band. A shelf by default.</Control>
<Control id="lowHz">The frequency of the first band.</Control>
<Control id="lowDb">The gain of the first band. A cut or a notch has none.</Control>
<Control id="lowQ">The width of the first band. A shelf has a fixed slope and ignores it.</Control>
<Control id="lowMidType">The shape of the second band. A bell by default.</Control>
<Control id="lowMidHz">The frequency of the second band.</Control>
<Control id="lowMidDb">The gain of the second band.</Control>
<Control id="lowMidQ">The width of the second band.</Control>
<Control id="highMidType">The shape of the third band. A bell by default.</Control>
<Control id="highMidHz">The frequency of the third band.</Control>
<Control id="highMidDb">The gain of the third band.</Control>
<Control id="highMidQ">The width of the third band.</Control>
<Control id="highType">The shape of the fourth band. A shelf by default.</Control>
<Control id="highHz">The frequency of the fourth band.</Control>
<Control id="highDb">The gain of the fourth band.</Control>
<Control id="highQ">The width of the fourth band.</Control>
<Control id="outputDb">The level of the output.</Control>
</DeviceControls>

## Panel

The curve is the sum of the four filters and the output level. Each band has a handle on it: drag it to set the frequency and the gain of the band, and it follows the pointer one to one. A cut or a notch has no gain, so its handle only slides. Double-click a handle to flatten the band.

Pick a band by its number or its handle, and its type, frequency, gain and width appear under the curve, the unused ones drawn off. Pass a `spectrum` (from `useSpectrum`, see [Analysis](/docs/engine/analysis#spectrum)) to draw the level of the sound behind the curve.

The type of a band decides what it uses:

| Type | Gain | Width |
| --- | --- | --- |
| Low cut, high cut | No | Yes |
| Notch | No | Yes |
| Low shelf, high shelf | Yes | No |
| Bell | Yes | Yes |

The cuts are 12 dB per octave, which is what a browser filter node does.

<DeviceUsage name="equalizer" />

## Engine

Create it, connect it and update it as for any device: see [The engine](/docs/devices#the-engine).

Four `BiquadFilterNode`s in series and an output gain. The curve is drawn from the same formulas the node runs, and the width of a band goes through `toWebAudioQ` on the way to the node, so the two agree. Off, every band goes flat and the output goes to 0 dB.

## Measure your engine

A host that brings its own engine can check it against what the panel draws. See [Measure your engine](/docs/devices#measure-your-engine): it works for the equalizer, the filter and the channel EQ.
